import { dateMs, isEmptyValue } from './calc';
import type { ColorRule, DateValue, FilterGroup, FilterOperator, FilterRule, OptionColor, PropertyDef, PropertyType, RowData } from './db-types';
import { t } from '@lumen/platform';

/** Reads a cell's normalised value (see `cellValue`). */
export type ValueOf = (row: RowData, prop: PropertyDef) => unknown;

export type FilterKind = 'text' | 'number' | 'select' | 'multi' | 'date' | 'checkbox' | 'person' | 'files' | 'relation';

export function filterKind(type: PropertyType): FilterKind {
  switch (type) {
    case 'number':
    case 'unique_id':
      return 'number';
    case 'select':
    case 'status':
      return 'select';
    case 'multi_select':
      return 'multi';
    case 'date':
    case 'created_time':
    case 'last_edited_time':
      return 'date';
    case 'checkbox':
      return 'checkbox';
    case 'person':
    case 'created_by':
    case 'last_edited_by':
      return 'person';
    case 'files':
      return 'files';
    case 'relation':
      return 'relation';
    default:
      return 'text';
  }
}

/** Operators offered per property type (the filter chip's condition menu). */
export function operatorsFor(type: PropertyType): FilterOperator[] {
  switch (filterKind(type)) {
    case 'text':
      return ['is', 'is_not', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'];
    case 'number':
      return ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'is_empty', 'is_not_empty'];
    case 'select':
      return ['is', 'is_not', 'is_empty', 'is_not_empty'];
    case 'multi':
    case 'person':
    case 'relation':
      return ['contains', 'not_contains', 'is_empty', 'is_not_empty'];
    case 'date':
      return ['is', 'before', 'after', 'on_or_before', 'on_or_after', 'within', 'is_empty', 'is_not_empty'];
    case 'checkbox':
      return ['is'];
    case 'files':
      return ['is_empty', 'is_not_empty'];
  }
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: t('Is'),
  is_not: t('Is not'),
  contains: t('Contains'),
  not_contains: t('Does not contain'),
  starts_with: t('Starts with'),
  ends_with: t('Ends with'),
  is_empty: t('Is empty'),
  is_not_empty: t('Is not empty'),
  eq: '=',
  neq: '≠',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
  before: t('Is before'),
  after: t('Is after'),
  on_or_before: t('Is on or before'),
  on_or_after: t('Is on or after'),
  within: t('Is within'),
};

const DAY = 86_400_000;

function startOfDay(t: number): number {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Resolves relative date tokens to a day start (ms). */
export function resolveDateToken(value: unknown, now: Date): number | null {
  const today = startOfDay(now.getTime());
  switch (value) {
    case 'today':
      return today;
    case 'tomorrow':
      return today + DAY;
    case 'yesterday':
      return today - DAY;
    case 'one_week_ago':
      return today - 7 * DAY;
    case 'one_week_from_now':
      return today + 7 * DAY;
    case 'one_month_ago':
      return new Date(new Date(today).setMonth(new Date(today).getMonth() - 1)).getTime();
    case 'one_month_from_now':
      return new Date(new Date(today).setMonth(new Date(today).getMonth() + 1)).getTime();
  }
  const t = dateMs(value);
  return t === null ? null : startOfDay(t);
}

function withinRange(token: unknown, now: Date): [number, number] | null {
  const today = startOfDay(now.getTime());
  const end = today + DAY - 1;
  const shift = (months: number) => new Date(new Date(today).setMonth(new Date(today).getMonth() + months)).getTime();
  switch (token) {
    case 'past_week':
      return [today - 7 * DAY, end];
    case 'past_month':
      return [shift(-1), end];
    case 'past_year':
      return [shift(-12), end];
    case 'next_week':
      return [today, today + 8 * DAY - 1];
    case 'next_month':
      return [today, shift(1) + DAY - 1];
    case 'next_year':
      return [today, shift(12) + DAY - 1];
  }
  return null;
}

function valueMs(v: unknown): number | null {
  if (v instanceof Date) return v.getTime();
  if (v && typeof v === 'object' && 'kind' in (v as object) && (v as { kind: string }).kind === 'range') return (v as { start: Date }).start.getTime();
  if (v && typeof v === 'object' && 'start' in (v as object)) return dateMs(v as DateValue);
  return dateMs(v);
}

function asText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(asText).join(', ');
  if (typeof v === 'object' && 'title' in (v as object)) return String((v as { title: string }).title);
  return String(v);
}

export interface FilterContext {
  me: string;
  now: Date;
}

/** Kind used for comparisons: formulas and rollups compare by their result. */
function kindOf(prop: PropertyDef, v: unknown): FilterKind {
  if (prop.type === 'formula' || prop.type === 'rollup') {
    if (typeof v === 'number') return 'number';
    if (typeof v === 'boolean') return 'checkbox';
    if (v instanceof Date || (v && typeof v === 'object' && 'kind' in (v as object))) return 'date';
    return 'text';
  }
  return filterKind(prop.type);
}

export function matchesRule(rule: FilterRule, row: RowData, props: PropertyDef[], valueOf: ValueOf, ctx: FilterContext): boolean {
  const prop = props.find((p) => p.id === rule.propId);
  if (!prop) return true;
  const v = valueOf(row, prop);
  if (rule.op === 'is_empty') return isEmptyValue(v);
  if (rule.op === 'is_not_empty') return !isEmptyValue(v);
  const kind = kindOf(prop, v);
  const target = rule.value;
  // An incomplete rule (no value yet) doesn't filter anything out, as in Notion.
  if ((target === undefined || target === null || target === '') && kind !== 'checkbox') return true;
  switch (kind) {
    case 'text': {
      const a = asText(v).toLowerCase();
      const b = asText(target).toLowerCase();
      if (rule.op === 'is') return a === b;
      if (rule.op === 'is_not') return a !== b;
      if (rule.op === 'contains') return a.includes(b);
      if (rule.op === 'not_contains') return !a.includes(b);
      if (rule.op === 'starts_with') return a.startsWith(b);
      if (rule.op === 'ends_with') return a.endsWith(b);
      return true;
    }
    case 'number': {
      if (v === null || v === undefined) return rule.op === 'neq';
      const a = Number(v);
      const b = Number(target);
      return { eq: a === b, neq: a !== b, gt: a > b, lt: a < b, gte: a >= b, lte: a <= b }[rule.op as 'eq'] ?? true;
    }
    case 'select':
      if (rule.op === 'is') return v === target;
      if (rule.op === 'is_not') return v !== target;
      return true;
    case 'multi':
    case 'relation':
    case 'person': {
      const list = (Array.isArray(v) ? v : v ? [v] : []) as string[];
      const want = target === 'me' ? ctx.me : String(target);
      if (rule.op === 'contains') return list.includes(want);
      if (rule.op === 'not_contains') return !list.includes(want);
      return true;
    }
    case 'checkbox':
      return (v === true) === (target === true || target === 'true');
    case 'date': {
      const t = valueMs(v);
      if (t === null) return false;
      const day = startOfDay(t);
      if (rule.op === 'within') {
        const r = withinRange(target, ctx.now);
        return !!r && t >= r[0] && t <= r[1];
      }
      const ref = resolveDateToken(target, ctx.now);
      if (ref === null) return true;
      if (rule.op === 'is') return day === ref;
      if (rule.op === 'before') return day < ref;
      if (rule.op === 'after') return day > ref;
      if (rule.op === 'on_or_before') return day <= ref;
      if (rule.op === 'on_or_after') return day >= ref;
      return true;
    }
    case 'files':
      return true;
  }
}

export function matchesFilter(filter: FilterGroup | null | undefined, row: RowData, props: PropertyDef[], valueOf: ValueOf, ctx: FilterContext): boolean {
  if (!filter || !filter.rules.length) return true;
  const test = (r: FilterRule | FilterGroup) => (r.kind === 'group' ? matchesFilter(r, row, props, valueOf, ctx) : matchesRule(r, row, props, valueOf, ctx));
  return filter.conj === 'and' ? filter.rules.every(test) : filter.rules.some(test);
}

export interface RowColors {
  row: OptionColor | null;
  cells: Record<string, OptionColor>;
}

/** Whether every rule of a filter has what it compares with (a filter matches everything until then; a colour nothing). */
function complete(f: FilterGroup | FilterRule): boolean {
  if (f.kind === 'group') return f.rules.length > 0 && f.rules.every(complete);
  return f.op === 'is_empty' || f.op === 'is_not_empty' || (f.value !== undefined && f.value !== null && f.value !== '');
}

/** A row's conditional colours (P-958): per target (the row, a property's cell) the first matching rule's colour. */
export function rowColors(rules: ColorRule[] | undefined, row: RowData, props: PropertyDef[], valueOf: ValueOf, ctx: FilterContext): RowColors {
  const out: RowColors = { row: null, cells: {} };
  for (const r of rules ?? []) {
    if (r.target === 'row' ? out.row : out.cells[r.target]) continue;
    if (!complete(r.when)) continue;
    if (!matchesFilter(r.when, row, props, valueOf, ctx)) continue;
    if (r.target === 'row') out.row = r.color;
    else out.cells[r.target] = r.color;
  }
  return out;
}

/** Depth of a filter tree (a flat filter is depth 1). Advanced filters allow up to 3. */
export function filterDepth(f: FilterGroup | FilterRule): number {
  if (f.kind === 'rule') return 0;
  return 1 + Math.max(0, ...f.rules.map(filterDepth));
}

export const MAX_FILTER_DEPTH = 3;

/** Adds a rule or nested group to the group at `path`, refusing to nest deeper than 3 levels. */
export function addToGroup(root: FilterGroup, path: number[], item: FilterRule | FilterGroup): FilterGroup {
  const copy = structuredClone(root);
  let g: FilterGroup = copy;
  for (const i of path) {
    const next = g.rules[i];
    if (!next || next.kind !== 'group') throw new Error('Invalid filter path');
    g = next;
  }
  g.rules.push(item);
  if (filterDepth(copy) > MAX_FILTER_DEPTH) throw new Error('Filters can be nested up to 3 levels deep');
  return copy;
}

/**
 * Values that make a new row satisfy the view's filter (where the condition says what to set): rows
 * created in a filtered view stay visible. Only AND conditions can be satisfied this way.
 */
export function valuesSatisfying(filter: FilterGroup | null | undefined, props: PropertyDef[], me: string, now: Date): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!filter) return out;
  const visit = (g: FilterGroup) => {
    if (g.conj !== 'and' && g.rules.length > 1) return;
    for (const r of g.rules) {
      if (r.kind === 'group') {
        visit(r);
        continue;
      }
      const prop = props.find((p) => p.id === r.propId);
      if (!prop || r.value === undefined || r.value === null || r.value === '') continue;
      if (['formula', 'rollup', 'created_time', 'created_by', 'last_edited_time', 'last_edited_by', 'unique_id', 'button'].includes(prop.type)) continue;
      const key = prop.type === 'title' ? '__title' : prop.id;
      const kind = filterKind(prop.type);
      if (kind === 'text' && (r.op === 'is' || r.op === 'contains' || r.op === 'starts_with' || r.op === 'ends_with')) out[key] = String(r.value);
      else if (kind === 'number') {
        const n = Number(r.value);
        if (r.op === 'eq' || r.op === 'gte' || r.op === 'lte') out[key] = n;
        else if (r.op === 'gt') out[key] = n + 1;
        else if (r.op === 'lt') out[key] = n - 1;
      } else if (kind === 'select' && r.op === 'is') out[key] = r.value;
      else if (kind === 'multi' && r.op === 'contains') out[key] = [...((out[key] as string[]) ?? []), r.value];
      else if ((kind === 'person' || kind === 'relation') && r.op === 'contains') out[key] = [...((out[key] as string[]) ?? []), r.value === 'me' ? me : r.value];
      else if (kind === 'checkbox' && r.op === 'is') out[key] = r.value === true || r.value === 'true';
      else if (kind === 'date' && (r.op === 'is' || r.op === 'on_or_before' || r.op === 'on_or_after')) {
        const t = resolveDateToken(r.value, now);
        if (t !== null) {
          const d = new Date(t);
          out[key] = { start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` };
        }
      }
    }
  };
  visit(filter);
  return out;
}
